import React, { useState, useCallback, useEffect } from 'react';
import { useMatch, useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { validateAddEditForm } from './validations';
import { parsePhoneNumber } from 'react-phone-number-input';
import Tabs from './components/Tabs';
import StockistDetailsTab from './components/StockistDetailsTab';
import BranchesTab from './components/BranchesTab';
import ProductsTab from './components/ProductsTab';
import Header from './components/Header';
import Buttons from './components/Buttons';
import useFetch from '../../../../hooks/useFetch';
import {
  APP_SUCCESS_MESSAGES,
  VALIDATION_FIELD_MAPPING,
} from '../../../../utils/appConstant';
import { AddEdit as AddEditInterface, AddEditApi, StockistResponse } from '../../../../interfaces/stockist.interface';
import { Validation } from '../../../../interfaces/common';
import StockistService from '../../../../services/stockist.service';
import CommonService from '../../../../services/common.service';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../../enums';
import Spinner from '../../../../components/Spinner';
import Toast from '../../../../components/Toast';
import { STATUS_CODE } from '../../../../utils/apiConstant';

const initialFormData: AddEditInterface = {
  name: '',
  address: '',
  city: '',
  state: '',
  zone: '',
  pincode: '',
  website: '',
  gstNumber: '',
  email: '',
  primaryContactPerson: '',
  primaryContactNumber: '',
  primaryContactEmail: '',
  status: true,
  logoPath: '',
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  city: '',
  state: '',
};

const AddEditView: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const view = !!useMatch(Modules.resources + AuthRoutes.stockists + RouteParamsTypes.view + '/:id');
  const edit = !!useMatch(Modules.resources + AuthRoutes.stockists + RouteParamsTypes.edit + '/:id');
  const navigate = useNavigate();

  const [tab, setTab] = useState<'stockistDetails' | 'branches' | 'products'>('stockistDetails');
  const [formData, setFormData] = useState<AddEditInterface>(initialFormData);
  const [dropdownSearchData, setDropdownSearchData] = useState<any>(initialDropdownSearchData);
  const [loading, setLoading] = useState(false);
  const [actionTwoLoading, setActionTwoLoading] = useState<boolean>(false);
  const [validation, setValidation] = useState<Validation>(initialValidation);

  useEffect(() => {
    setTab(location?.state?.tab ?? 'stockistDetails');
  }, [location.state, location.key]);

  const { data: zones } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'zone',
      searchText: '',
    },
    dependency: [],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'city',
      searchText: '',
    },
    dependency: [],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'city',
      searchText: dropdownSearchData.city || '',
    },
    dependency: [dropdownSearchData.city],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'state',
      searchText: '',
    },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'geoLookup',
    payload: {
      type: 'state',
      searchText: dropdownSearchData.state || '',
    },
    dependency: [dropdownSearchData.state],
  });

  useEffect(() => {
    setFormData(initialFormData);
    setDropdownSearchData(initialDropdownSearchData);
    setValidation(initialValidation);
  }, [location.key]);

  const { loading: getContentLoading } = useFetch({
    service: StockistService,
    func: 'get',
    payload: id,
    dependency: [id, location.key],
    skip: !id,
    onSuccess: async (response: StockistResponse) => {
      setFormData({
        name: response.name,
        address: response.address,
        city: response.city?.code,
        state: response.state?.code,
        zone: response.zone?.code,
        pincode: response.pincode,
        website: response.website,
        gstNumber: response.gst_number,
        email: response.email,
        primaryContactPerson: response.primary_contact_person,
        primaryContactNumber: response.primary_contact_number,
        primaryContactEmail: response.primary_contact_email,
        status: response.is_active,
        logoPath: response.logo_path,
      });
    },
    onError: (error: any) => {
      // Redirect to list if not found
      if (error.code === STATUS_CODE.notFound) navigate(Modules.resources + AuthRoutes.stockists);
    },
  });

  const handleTabChange = useCallback((e: any, value: 'stockistDetails' | 'branches' | 'products') => {
    setTab(value);
  }, []);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      if (name === VALIDATION_FIELD_MAPPING.primaryContactNumber) {
        setFormData((prev) => {
          return {
            ...prev,
            [name]: event.target.value,
          };
        });
      } else {
        let value: any;
        if (name === VALIDATION_FIELD_MAPPING.status) {
          value = event.target.checked;
        } else {
          value = event.target.value;
        }

        setFormData((prev) => {
          return {
            ...prev,
            [name]: value,
          };
        });
      }
    },
    [setFormData]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateAddEditForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (action: '1' | '2', event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateAddEditForm(formData);
      setValidation(newValidation);
      if (!newValidation.validate) return;

      if (action === '1') setLoading(true);
      else setActionTwoLoading(true);

      try {
        const payload: AddEditApi = {
          name: formData.name.trim(),
          address: formData.address.trim(),
          city: formData.city,
          state: formData.state,
          zone: formData.zone,
          pincode: formData.pincode.trim(),
          website: formData.website?.trim() || '',
          gst_number: formData.gstNumber.trim(),
          email: formData.email.trim(),
          primary_contact_person: formData.primaryContactPerson.trim(),
          primary_contact_number: formData.primaryContactNumber.trim(),
          primary_contact_email: formData.primaryContactEmail.trim(),
          is_active: formData.status,
          logo_path: formData.logoPath,
        };

        let response: any;
        if (!edit) {
          response = await StockistService.add(payload);
        } else if (id) {
          response = await StockistService.edit(id, payload);
        }

        if (response.success !== false) {
          toast.success(
            <Toast
              message={edit ? APP_SUCCESS_MESSAGES.stockistUpdated : APP_SUCCESS_MESSAGES.stockistCreated}
            />,
            { icon: false }
          );

          if (edit) {
            navigate(Modules.resources + AuthRoutes.stockists);
          } else if (action === '2') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Focus on first input if possible?
            setFormData(initialFormData); // Reset form
            navigate(Modules.resources + AuthRoutes.stockists + RouteParamsTypes.add, {
              replace: true,
            });
          } else {
            navigate(`${Modules.resources}${AuthRoutes.stockists}`, { replace: true });
          }
        }

        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      } catch (err: any) {
        if (action === '1') setLoading(false);
        else setActionTwoLoading(false);
      }
    },
    [id, edit, formData, navigate]
  );

  const handleKeyDown = useCallback(
    (e: any, name?: string) => {
      if (e.key === 'Enter') handleSubmit('1');
    },
    [handleSubmit]
  );

  const handleCancel = () => {
    navigate(Modules.resources + AuthRoutes.stockists);
  };

  let formContent;

  if (getContentLoading) {
    formContent = (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  } else if (tab === 'stockistDetails') {
    formContent = (
      <StockistDetailsTab
        edit={edit}
        view={view}
        zones={zones || []}
        allCities={allCities || []}
        cities={cities || []}
        allStates={allStates || []}
        states={states || []}
        formData={formData}
        validation={validation}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onKeyDown={handleKeyDown}
        onInputBlur={handleInputBlur}
        onInputChange={handleInputChange}
      />
    );
  } else if (tab === 'branches') {
    formContent = (
      <BranchesTab stockistId={id!} />
    );
  } else if (tab === 'products') {
    formContent = (
      <ProductsTab stockistId={id!} />
    );
  }

  return (
    <>
      <Header id={id} view={view} edit={edit} />

      <div className="card mb-6">
        <h5 className="card-header">
          {!id
            ? t('stockists.addEdit.pageTexts.title.addTitle')
            : view
              ? t('stockists.addEdit.pageTexts.title.viewTitle')
              : t('stockists.addEdit.pageTexts.title.editTitle')}
        </h5>

        <hr className="m-0"></hr>

        <Tabs tab={tab} add={!edit && !view} onTabChange={handleTabChange} />

        <hr className="m-0"></hr>

        {formContent}

        <hr className="m-0"></hr>

        <Buttons
          tab={tab}
          actionTwoLoading={actionTwoLoading}
          loading={loading}
          view={view}
          edit={edit}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );
};

export default AddEditView;
